import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { TrendUp, TrendDown, Users, CalendarCheck, Warning, ChartBar } from '@phosphor-icons/react';

const API = process.env.REACT_APP_BACKEND_URL;
const PERIODS = [
  { value: 'week', label: 'Semana' },
  { value: 'month', label: 'Mês' },
  { value: 'quarter', label: 'Trimestre' },
  { value: 'semester', label: 'Semestre' },
  { value: 'year', label: 'Ano' },
];

function MetricCard({ label, value, previous, icon: Icon, color = 'text-brand-green' }) {
  const diff = previous != null ? value - previous : null;
  const pctChange = previous ? Math.round(((value - previous) / Math.max(previous, 1)) * 100) : null;
  return (
    <div className="bg-white border border-brand-border rounded-2xl p-5" data-testid={`viewer-metric-${label.toLowerCase().replace(/\s/g, '-')}`}>
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs text-brand-muted font-medium uppercase tracking-wider">{label}</span>
        {Icon && <Icon size={18} className="text-brand-placeholder" weight="duotone" />}
      </div>
      <p className={`text-3xl font-heading font-bold ${color}`}>{value}</p>
      {diff !== null && (
        <div className="flex items-center gap-1 mt-1.5">
          {diff >= 0 ? <TrendUp size={12} className="text-brand-green" /> : <TrendDown size={12} className="text-red-500" />}
          <span className={`text-[11px] font-medium ${diff >= 0 ? 'text-brand-green' : 'text-red-500'}`}>{diff >= 0 ? '+' : ''}{diff} ({pctChange >= 0 ? '+' : ''}{pctChange}%)</span>
          <span className="text-[10px] text-brand-muted">vs anterior</span>
        </div>
      )}
    </div>
  );
}

export default function ViewerDashboardPage() {
  const [data, setData] = useState(null);
  const [satisfaction, setSatisfaction] = useState(null);
  const [period, setPeriod] = useState('month');
  const [loading, setLoading] = useState(true);

  const fetch = useCallback(async () => {
    setLoading(true);
    try {
      const [dRes, sRes] = await Promise.all([
        axios.get(`${API}/api/ceo/dashboard?period=${period}`),
        axios.get(`${API}/api/ceo/satisfaction`),
      ]);
      setData(dRes.data); setSatisfaction(sRes.data);
    } catch {} finally { setLoading(false); }
  }, [period]);
  useEffect(() => { fetch(); }, [fetch]);

  if (loading && !data) return <div className="text-center py-12 text-brand-muted">A carregar dashboard...</div>;

  return (
    <div className="fade-in" data-testid="viewer-dashboard-page">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h2 className="font-heading font-medium text-2xl text-brand-text">Dashboard CEO</h2>
          <p className="text-sm text-brand-muted mt-1">Visão geral agregada do negócio</p>
        </div>
        <div className="flex gap-1.5">
          {PERIODS.map(p => (
            <button key={p.value} onClick={() => setPeriod(p.value)} className={`px-3 py-1.5 rounded-full text-xs font-medium ${period === p.value ? 'bg-brand-green text-white' : 'bg-brand-bg text-brand-muted border border-brand-border'}`} data-testid={`viewer-period-${p.value}`}>{p.label}</button>
          ))}
        </div>
      </div>

      {data && (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <MetricCard label="Novos clientes" value={data.new_clients.current} previous={data.new_clients.previous} icon={Users} />
            <MetricCard label="Consultas" value={data.consultations.current} previous={data.consultations.previous} icon={CalendarCheck} />
            <MetricCard label="Cancelamentos" value={data.cancellations.current} previous={data.cancellations.previous} icon={Warning} color="text-red-500" />
            <MetricCard label="Clientes ativos" value={data.active_clients_total} icon={ChartBar} />
          </div>

          {satisfaction && satisfaction.total > 0 && (
            <div className="bg-white border border-brand-border rounded-2xl p-5 mb-6" data-testid="viewer-satisfaction">
              <h3 className="font-heading font-medium text-sm text-brand-muted uppercase tracking-wider mb-3">Satisfação</h3>
              <div className="flex items-center gap-6">
                <div className="text-center"><p className="text-3xl font-heading font-bold text-brand-green">{satisfaction.average}</p><p className="text-[10px] text-brand-muted">Média</p></div>
                <div className="text-center"><p className="text-2xl font-heading font-bold text-brand-text">{satisfaction.total}</p><p className="text-[10px] text-brand-muted">Feedbacks</p></div>
                <div className="text-center"><p className="text-2xl font-heading font-bold text-brand-green">{satisfaction.positive_pct}%</p><p className="text-[10px] text-brand-muted">Positivos</p></div>
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
            <div className="bg-white border border-brand-border rounded-2xl p-5" data-testid="viewer-by-program">
              <h3 className="font-heading font-medium text-sm text-brand-muted uppercase tracking-wider mb-4">Clientes por programa</h3>
              {Object.keys(data.by_program || {}).length === 0 ? <p className="text-xs text-brand-placeholder">Sem dados.</p> : (
                <div className="space-y-3">
                  {Object.entries(data.by_program).sort((a, b) => b[1] - a[1]).map(([program, count]) => (
                    <div key={program} className="flex items-center justify-between">
                      <span className="text-sm text-brand-text">{program}</span>
                      <div className="flex items-center gap-2">
                        <div className="w-24 h-2 bg-brand-bg rounded-full overflow-hidden"><div className="h-full bg-brand-green rounded-full" style={{ width: `${Math.min(100, (count / Math.max(data.active_clients_total, 1)) * 100)}%` }} /></div>
                        <span className="text-sm font-semibold text-brand-text w-8 text-right">{count}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="bg-white border border-brand-border rounded-2xl p-5" data-testid="viewer-retention">
              <h3 className="font-heading font-medium text-sm text-brand-muted uppercase tracking-wider mb-4">Retenção</h3>
              <div className="flex items-baseline gap-2">
                <span className="text-4xl font-heading font-bold text-brand-green">{data.retention_rate}%</span>
                <span className="text-sm text-brand-muted">taxa de retenção</span>
              </div>
              <p className="text-xs text-brand-muted mt-3">{data.at_risk_count} cliente{data.at_risk_count !== 1 ? 's' : ''} em risco de abandono</p>
            </div>
          </div>

          {data.consults_by_nutri?.length > 0 && (
            <div className="bg-white border border-brand-border rounded-2xl p-5 mb-6" data-testid="viewer-consults-by-nutri">
              <h3 className="font-heading font-medium text-sm text-brand-muted uppercase tracking-wider mb-4">Consultas por profissional</h3>
              <div className="space-y-3">
                {(() => { const max = Math.max(1, ...data.consults_by_nutri.map(n => n.total)); return data.consults_by_nutri.map(n => (
                  <div key={n.nutri_id} className="flex items-center gap-3">
                    <span className="w-28 shrink-0 text-sm text-brand-text truncate">{n.name}</span>
                    <div className="flex-1 h-5 bg-brand-bg rounded-full overflow-hidden"><div className="h-full bg-brand-green rounded-full flex items-center justify-end pr-2" style={{ width: `${Math.max(8, (n.total / max) * 100)}%` }}><span className="text-[10px] font-semibold text-white">{n.total}</span></div></div>
                    <span className="w-24 shrink-0 text-right text-[11px] text-brand-muted">{n.weekly_avg}/semana</span>
                  </div>
                )); })()}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
